import { Fragment } from "react";
import { styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import { SidePanelContainer, SidePanelHeader, SidePanelSection, DateColumn, DatedListDivider } from "../ui";
import { ChevronRight, SignalsIcon, TodosIcon } from "@acto/web-common/components";
import { LoadMoreButton } from "../../DataTable/ui/LoadMoreButton";
import { Flex, Chip } from "../../primitives";
import { Truncate } from "../../Truncate";

interface CompletedAppointment {
  id: string;
  date: string;
  title: string | null;
  clientExternalReference: string | null;
  todoCount: number;
  description: string;
  clientId: string | null;
  clientAccessible: boolean;
  noteId: string | null;
  fromSignal?: boolean;
}

interface CompletedAppointmentsData {
  entityName: string;
  dateRange: string;
  completedCount: number;
  appointments: CompletedAppointment[];
}

interface CompletedAppointmentsSidePanelProps {
  data: CompletedAppointmentsData;
  heading?: string;
  countSuffix?: string;
  dateColor?: "default" | "error";
  onAppointmentClick?: (noteId: string | null, clientId: string) => void;
  onCustomerClick?: (customerId: string, customerName: string) => void;
  hasMore?: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
}

const AppointmentCard = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.managementView.spacing[16],
  alignItems: "start",
  textAlign: "left",
  padding: theme.managementView.spacing[12],
  borderRadius: theme.managementView.radius[10],
  marginLeft: `-${theme.managementView.spacing[12]}`,

  "&[data-clickable='true']": {
    cursor: "pointer",
    transition: "background-color 0.2s",
    "&:hover": {
      backgroundColor: theme.managementView.colors.neutral[4],
    },
  },
}));

const AppointmentTitle = styled("span")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.managementView.spacing[8],
  width: "100%",
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[15],
  fontWeight: theme.managementView.font.weight[500],
  color: theme.managementView.colors.neutral[90],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  flex: 1,
  textAlign: "left",

  "& svg": {
    color: theme.managementView.colors.neutral[56],
    flexShrink: 0,
    width: "10px",
    height: "10px",
  },
}));

const ClickableTitle = styled("span")({
  cursor: "pointer",
  "&:hover": {
    textDecoration: "underline",
  },
});

const SecondaryLine = styled("span")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[400],
  color: theme.managementView.colors.neutral[56],
}));

const MetaRow = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: theme.managementView.spacing[12],
  paddingTop: theme.managementView.spacing[4],
}));

const MetaItem = styled("span")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.managementView.spacing[8],
}));

const MetaIcon = styled("span")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "20px",
  height: "20px",
  flexShrink: 0,
  color: theme.managementView.colors.neutral[56],

  "& svg": {
    width: "18px",
    height: "18px",
  },

  "&[data-size='small'] svg": {
    width: "16px",
    height: "16px",
  },
}));

const Description = styled("p")(({ theme }) => ({
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[400],
  color: theme.managementView.colors.neutral[56],
  margin: 0,
  lineHeight: "1.5",
  textAlign: "left",
}));

export const CompletedAppointmentsSidePanel = ({
  data,
  heading,
  countSuffix,
  dateColor = "default",
  onAppointmentClick,
  onCustomerClick,
  hasMore,
  onLoadMore,
  isLoadingMore,
}: CompletedAppointmentsSidePanelProps) => {
  const t = useTranslations();
  return (
    <SidePanelContainer>
      <SidePanelHeader
        title={heading ?? t("managementView.completedAppointments.title")}
        subtitle={`${data.entityName} • ${data.dateRange}`}
      />

      <SidePanelSection
        title={`${data.completedCount} ${countSuffix ?? t("managementView.completedAppointments.completed")}`}
      >
        <Flex direction="column">
          {data.appointments.map((appointment, index) => {
            const { clientId, noteId } = appointment;
            const title = appointment.title ?? t("managementView.deletedClient");
            const hasDescription = Boolean(appointment.description?.trim());
            const openAppointment =
              onAppointmentClick && appointment.clientAccessible && clientId != null
                ? () => onAppointmentClick(noteId, clientId)
                : undefined;
            const openCustomer =
              onCustomerClick && appointment.clientAccessible && clientId != null
                ? () => onCustomerClick(clientId, title)
                : undefined;

            return (
              <Fragment key={appointment.id}>
                <AppointmentCard data-clickable={Boolean(openAppointment)} onClick={openAppointment}>
                  <DateColumn>
                    <Chip size="large" color={dateColor}>
                      {appointment.date}
                    </Chip>
                  </DateColumn>
                  <Flex direction="column" align="start" gap={12} style={{ flex: 1, minWidth: 0 }}>
                    <Flex direction="column" gap={8} align="start" style={{ width: "100%" }}>
                      <AppointmentTitle>
                        {openCustomer ? (
                          <ClickableTitle
                            onClick={(e) => {
                              e.stopPropagation();
                              openCustomer();
                            }}
                          >
                            {title}
                          </ClickableTitle>
                        ) : (
                          title
                        )}
                        {Boolean(openAppointment) && <ChevronRight />}
                      </AppointmentTitle>
                      {appointment.clientExternalReference && (
                        <SecondaryLine>#{appointment.clientExternalReference}</SecondaryLine>
                      )}
                    </Flex>
                    {hasDescription && (
                      <Description>
                        <Truncate lines={2}>{appointment.description}</Truncate>
                      </Description>
                    )}
                    {(appointment.todoCount > 0 || appointment.fromSignal) && (
                      <MetaRow>
                        {appointment.todoCount > 0 && (
                          <MetaItem>
                            <MetaIcon aria-hidden>
                              <TodosIcon />
                            </MetaIcon>
                            <SecondaryLine>
                              {appointment.todoCount} {t("managementView.completedAppointments.todos")}
                            </SecondaryLine>
                          </MetaItem>
                        )}
                        {appointment.fromSignal && (
                          <MetaItem>
                            <MetaIcon aria-hidden data-size="small">
                              <SignalsIcon />
                            </MetaIcon>
                            <SecondaryLine>{t("managementView.completedAppointments.fromSignal")}</SecondaryLine>
                          </MetaItem>
                        )}
                      </MetaRow>
                    )}
                  </Flex>
                </AppointmentCard>
                {index < data.appointments.length - 1 && <DatedListDivider />}
              </Fragment>
            );
          })}
        </Flex>
        {hasMore && onLoadMore && (
          <LoadMoreButton onClick={onLoadMore} label={t("common.loadMore")} isLoading={isLoadingMore} />
        )}
      </SidePanelSection>
    </SidePanelContainer>
  );
};
